import { Caption1, Checkbox, Menu, MenuDivider, MenuItem, MenuList, MenuPopover, MenuTrigger, Tooltip, makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import { Add16Regular, AddCircle16Regular, Checkmark16Regular, ChevronDown12Regular, ChevronUp12Regular, LinkDismiss16Regular, MoreHorizontal16Regular, Open16Regular, TaskListSquareLtr16Regular } from "@fluentui/react-icons";
import { useState } from "react";
import { FuiButton } from "@/components/fui-control";
import type { ExecutionTask } from "@/engine/types";
import { DetailSection } from "@/features/tasks/detail-parts";
import { AssigneeAvatars, useTaskContext } from "@/features/tasks/task-ui";
import { formatDate, formatDaysUntil } from "@/lib/date-format";
import { isOverdue } from "@/store/execution-task-ops";
import { useProjectStore } from "@/store/project-store";
import { RefSearchAdd, type RefOption } from "./record-ui";

/** Open rows shown before 「나머지 n개 보기」. */
const OPEN_LIMIT = 5;
/** Past this many visible rows the list scrolls inside the section instead of stretching the sheet. */
const SCROLL_AFTER = 16;
const ROW_HEIGHT = 32;

const MORE_CLASS = "sg-task-more";

const useStyles = makeStyles({
  summary: { display: "inline-flex", alignItems: "center", gap: tokens.spacingHorizontalXS, color: tokens.colorNeutralForeground3, whiteSpace: "nowrap" },
  summaryAlert: { color: tokens.colorNeutralForeground1 },
  headActions: { display: "inline-flex", alignItems: "center", gap: tokens.spacingHorizontalXS },
  list: { listStyleType: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" },
  scroll: { overflowY: "auto", maxHeight: `${SCROLL_AFTER * ROW_HEIGHT}px` },
  row: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXXS,
    minWidth: 0,
    minHeight: `${ROW_HEIGHT}px`,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    [`:hover .${MORE_CLASS}`]: { opacity: 1 },
    [`:focus-within .${MORE_CLASS}`]: { opacity: 1 },
  },
  open: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flex: 1,
    minWidth: 0,
    alignSelf: "stretch",
    padding: `0 ${tokens.spacingHorizontalXS}`,
    ...shorthands.border("none"),
    background: "none",
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  title: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: tokens.fontSizeBase300 },
  titleDone: { color: tokens.colorNeutralForeground3, textDecorationLine: "line-through" },
  doneLabel: { flexShrink: 0, color: tokens.colorNeutralForeground3, whiteSpace: "nowrap" },
  /** D-day가 들어가는 고정 칸. 짧은 값도 오른쪽 끝이 같다. */
  due: {
    flexShrink: 0,
    width: "7ch",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
  },
  more: { opacity: 0, "@media (hover: none)": { opacity: 1 } },
  moreOpen: { opacity: 1 },
  /** disclosure and add rows sit on the same text column as task titles (after the check) */
  quiet: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    width: "100%",
    minHeight: `${ROW_HEIGHT}px`,
    padding: `0 ${tokens.spacingHorizontalS}`,
    ...shorthands.border("none"),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    background: "none",
    color: tokens.colorNeutralForeground3,
    font: "inherit",
    fontSize: tokens.fontSizeBase300,
    textAlign: "left",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover, color: tokens.colorNeutralForeground2 },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  addIcon: { display: "inline-flex", width: "20px", justifyContent: "center", color: tokens.colorBrandForeground1 },
  chevron: { display: "inline-flex", width: "20px", justifyContent: "center" },
});

/**
 * 기록 시트의 「대응 Task」. Reminders처럼 한 목록 안에서 읽고·체크하고·추가한다.
 * - 요약은 머리 한 곳(남은 개수, 지연 수는 본문색). 완료는 접어 둔다.
 * - 행: 체크 · 제목 · 담당 아바타 · D-day. 마감이 없거나 완료면 —. 나머지 동작은 ⋯/우클릭 메뉴.
 * - 마지막 줄 「Task 추가 또는 연결」 하나로 기존 연결과 새로 만들기를 함께 받는다.
 */
export function RecordTaskSection({
  tasks,
  options,
  canCreate,
  readOnly = false,
  onOpen,
  onCreateDetailed,
  onQuickAdd,
  onLink,
  onUnlink,
}: {
  /** linked Tasks (record order) */
  tasks: ExecutionTask[];
  /** every Task in the project, for the link search */
  options: RefOption[];
  /** false for a record that is not saved yet: link only */
  canCreate: boolean;
  /** 열기만 남기고 완료·연결·만들기는 숨긴다. */
  readOnly?: boolean;
  onOpen: (taskId: string) => void;
  onCreateDetailed: () => void;
  onQuickAdd: (title: string) => void;
  onLink: (taskId: string) => void;
  onUnlink: (taskId: string) => void;
}) {
  const styles = useStyles();
  const { today } = useTaskContext();
  const [showAllOpen, setShowAllOpen] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [adding, setAdding] = useState(false);

  // open: overdue → earliest due → no due. done: most recently completed first.
  const open = tasks.filter((t) => t.status !== "done").sort((a, b) => (a.due ?? "9999-12-31").localeCompare(b.due ?? "9999-12-31"));
  const done = tasks.filter((t) => t.status === "done").sort((a, b) => (b.completedAt ?? "").localeCompare(a.completedAt ?? ""));
  const overdue = open.filter((t) => isOverdue(t, today)).length;
  const visibleOpen = showAllOpen ? open : open.slice(0, OPEN_LIMIT);
  const hiddenOpen = open.length - visibleOpen.length;
  const rows = [...visibleOpen, ...(showDone ? done : [])];

  if (readOnly && tasks.length === 0) return null;

  const summary =
    tasks.length === 0 ? null : open.length === 0 ? (
      <span className={styles.summary}>모두 완료</span>
    ) : (
      <span className={styles.summary}>
        {open.length}개 남음
        {overdue > 0 && <span className={styles.summaryAlert}>· 지연 {overdue}</span>}
      </span>
    );

  return (
    <DetailSection
      icon={<TaskListSquareLtr16Regular />}
      title="대응 Task"
      action={
        <span className={styles.headActions}>
          {summary}
          {!readOnly && canCreate && (
            <Tooltip content="Task 만들기 (상세 입력)" relationship="label">
              <FuiButton appearance="subtle" density="dialog" icon={<AddCircle16Regular />} onClick={onCreateDetailed} />
            </Tooltip>
          )}
        </span>
      }
    >
      <div>
        {rows.length > 0 && (
          <ul className={mergeClasses(styles.list, rows.length > SCROLL_AFTER && styles.scroll)} aria-label="대응 Task">
            {rows.map((t) => (
              <TaskRow key={t.id} task={t} today={today} readOnly={readOnly} onOpen={() => onOpen(t.id)} onUnlink={() => onUnlink(t.id)} />
            ))}
          </ul>
        )}

        {(hiddenOpen > 0 || showAllOpen) && open.length > OPEN_LIMIT && (
          <button type="button" className={styles.quiet} aria-expanded={showAllOpen} onClick={() => setShowAllOpen((v) => !v)}>
            <span className={styles.chevron}>{showAllOpen ? <ChevronUp12Regular /> : <ChevronDown12Regular />}</span>
            {showAllOpen ? "접기" : `나머지 ${hiddenOpen}개 보기`}
          </button>
        )}
        {done.length > 0 && (
          <button type="button" className={styles.quiet} aria-expanded={showDone} onClick={() => setShowDone((v) => !v)}>
            <span className={styles.chevron}>{showDone ? <ChevronUp12Regular /> : <ChevronDown12Regular />}</span>
            {showDone ? "완료 항목 숨기기" : `완료된 ${done.length}개 보기`}
          </button>
        )}

        {!readOnly &&
          (adding ? (
            <RefSearchAdd
              autoFocus
              exclude={tasks.map((t) => t.id)}
              options={options}
              onPick={onLink}
              onCreate={canCreate ? onQuickAdd : undefined}
              onDismiss={() => setAdding(false)}
              placeholder={canCreate ? "새 Task 제목 또는 기존 Task 검색" : "기존 Task 검색"}
              ariaLabel="대응 Task 추가 또는 연결"
              createLabel={(text) => `「${text}」 만들기`}
            />
          ) : (
            <button type="button" className={styles.quiet} onClick={() => setAdding(true)}>
              <span className={styles.addIcon}>
                <Add16Regular />
              </span>
              {canCreate ? "Task 추가 또는 연결" : "Task 연결"}
            </button>
          ))}
      </div>
    </DetailSection>
  );
}

function TaskRow({
  task,
  today,
  readOnly = false,
  onOpen,
  onUnlink,
}: {
  task: ExecutionTask;
  today: string;
  readOnly?: boolean;
  onOpen: () => void;
  onUnlink: () => void;
}) {
  const styles = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const isDone = task.status === "done";
  const title = task.title || "제목 없는 Task";
  const toggleDone = () => useProjectStore.getState().setExecutionStatus(task.id, isDone ? "notStarted" : "done");

  return (
    <li
      className={styles.row}
      onContextMenu={(e) => {
        if (readOnly) return;
        e.preventDefault();
        setMenuOpen(true);
      }}
    >
      {readOnly ? (
        isDone ? <Caption1 className={styles.doneLabel}>완료</Caption1> : null
      ) : (
        <Checkbox shape="circular" checked={isDone} onChange={toggleDone} aria-label={`${title} ${isDone ? "완료 취소" : "완료"}`} />
      )}
      <button type="button" className={styles.open} onClick={onOpen} title={title}>
        <span className={mergeClasses(styles.title, isDone && styles.titleDone)}>{title}</span>
        {task.assignees.length > 0 && <AssigneeAvatars names={task.assignees} size={20} max={1} />}
        <DueMark due={task.due} today={today} done={isDone} />
      </button>
      {!readOnly && (
      <Menu open={menuOpen} onOpenChange={(_, d) => setMenuOpen(d.open)} positioning="below-end">
        <MenuTrigger disableButtonEnhancement>
          <FuiButton
            className={mergeClasses(MORE_CLASS, styles.more, menuOpen && styles.moreOpen)}
            appearance="subtle"
            density="dialog"
            icon={<MoreHorizontal16Regular />}
            aria-label={`${title} 동작`}
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<Open16Regular />} onClick={onOpen}>
              열기
            </MenuItem>
            <MenuItem icon={<Checkmark16Regular />} onClick={toggleDone}>
              {isDone ? "완료 취소" : "완료"}
            </MenuItem>
            <MenuDivider />
            <MenuItem icon={<LinkDismiss16Regular />} onClick={onUnlink}>
              연결 해제
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
      )}
    </li>
  );
}

/** 열린 작업만 D-day. 완료이거나 마감이 없으면 같은 너비의 —. */
function DueMark({ due, today, done }: { due?: string; today: string; done: boolean }) {
  const styles = useStyles();
  const label = !done && due ? formatDaysUntil(due, today) : "—";
  const tip = due ? formatDate(due, "full") : "마감 없음";
  return (
    <Tooltip content={tip} relationship="description">
      <Caption1 className={styles.due}>{label}</Caption1>
    </Tooltip>
  );
}
